/**
 * Coign SDK — public API entry point.
 *
 * Progressive lifecycle: init() mounts the shell immediately (never awaits
 * model download). warmUp()/ask()/open engage the quick model, then upgrade
 * to the full model in the background.
 */

import type {
  AskOptions,
  CoignConfig,
  CoignSDK,
  ToolDefinition,
  CoignEvent,
  CoignError,
  ModelTier,
} from './types.js';
import {
  loadModel,
  destroyEngine,
  getIsLoading,
  getTier,
  hasModelCached as engineHasModelCached,
  chatCompletion,
  resolveFullModelId,
  cancelEngineInit,
  preloadWorkerEngine,
} from './core/engine.js';
import { checkSupport } from './core/capabilities.js';
import { resolvePreset, resolvePresetName } from './core/presets.js';
import { buildPageOutline } from './core/context.js';
import { registerBuiltin, registerHost, setHostNamespace } from './tools/registry.js';
import { searchPageTool, invalidateSearchIndex } from './tools/built-in/search-page.js';
import { getPageOutlineTool } from './tools/built-in/get-outline.js';
import { getElementTool } from './tools/built-in/get-element.js';
import { runManualToolLoop } from './llm/manual-tool-loop.js';
import { getCachedPrompts, invalidatePromptCache } from './llm/prompt-cache.js';
import { emit, on, clearAllListeners } from './events.js';
import { patchConfig, resetConfig, getConfig, DEFAULT_PROMPT } from './config.js';
import { loadHistory, saveHistory, clearHistory, exportHistory } from './store/history.js';
import { outlineStore } from './store/outline-store.js';
import {
  loadWidgetUi,
  createWidget as createWidgetUI,
  destroyWidget as destroyWidgetUI,
  showWidget as showWidgetUI,
  hideWidget as hideWidgetUI,
  openPanel,
  closePanel,
  mountInline,
  unmountInline,
  addAssistantMessage,
  addToolCallMessage,
  addToolResultMessage,
  applyTheme,
  setThinking,
  startAssistantStream,
  appendStreamToken,
  finishAssistantStream,
  setStatus,
  setUpgradeStatus,
  setOnOpenCallback,
} from './ui/widget-loader.js';
import { showConfirmDialog } from './ui/modal.js';
import { createRealAPI, replayQueue, resetQueueState, type GlobalAPI } from './queue-loader.js';

declare const __VERSION__: string;

export const VERSION = typeof __VERSION__ !== 'undefined' ? __VERSION__ : '0.1.0';

let sdkInstance: CoignSDK | null = null;
let initialized = false;
let eventUnsubs: Array<() => void> = [];
let api: GlobalAPI;
let warmUpPromise: Promise<void> | null = null;
let upgradePromise: Promise<void> | null = null;
let askQueue: Promise<unknown> = Promise.resolve();
/** When true, the next `answer` event should not add a widget message (already streamed). */
let streamedAnswerPending = false;
let asksInFlight = 0;
let idleUpgradeHandle: ReturnType<typeof setTimeout> | null = null;

function ensureOutline(): void {
  const cfg = getConfig();
  if (!outlineStore.get('current')) {
    const outline = buildPageOutline(cfg.selectDOM ?? 'main, [role="main"], article');
    outlineStore.set('current', outline);
    invalidatePromptCache();
  }
}

/** Build outline at shell init only when engage is eager; otherwise on first ask/warmUp. */
function maybeOutlineAtInit(): void {
  const cfg = getConfig();
  if (cfg.engage === 'init' || cfg.engage === 'open') {
    ensureOutline();
  }
}

function wireConfigCallbacks(config: Partial<CoignConfig>): void {
  if (config.onLoad) eventUnsubs.push(on('load', config.onLoad));
  if (config.onReady) eventUnsubs.push(on('ready', config.onReady));
  if (config.onError) eventUnsubs.push(on('error', config.onError));
  if (config.onAsk) eventUnsubs.push(on('ask', config.onAsk));
  if (config.onAnswer) eventUnsubs.push(on('answer', config.onAnswer));
  if (config.onWarn) eventUnsubs.push(on('warn', config.onWarn));
  if (config.onToolCall) eventUnsubs.push(on('toolCall', config.onToolCall));
  if (config.onToolResult) eventUnsubs.push(on('toolResult', config.onToolResult));
  if (config.onDownloadProgress) eventUnsubs.push(on('downloadProgress', config.onDownloadProgress));
  if (config.onDownloadComplete) eventUnsubs.push(on('downloadComplete', config.onDownloadComplete));
  if (config.onDownloadError) eventUnsubs.push(on('downloadError', config.onDownloadError));
  if (config.onToken) eventUnsubs.push(on('token', config.onToken));
  if (config.onShellReady) eventUnsubs.push(on('shellReady', config.onShellReady));
  if (config.onModelReady) eventUnsubs.push(on('modelReady', config.onModelReady));
  if (config.onUpgraded) eventUnsubs.push(on('upgraded', config.onUpgraded));
}

function markApiReady(): void {
  if (!api) return;
  api._ready = true;
  const cbs = api._callbacks.slice();
  api._callbacks = [];
  cbs.forEach((cb) => {
    try {
      cb(api);
    } catch (e) {
      console.error('[Coign] Callback error:', e);
    }
  });
}

/**
 * Ensure quick (or full-only) model is loaded. Idempotent.
 * Full upgrade is deferred until after the first quick answer or idle.
 */
export async function warmUp(): Promise<void> {
  if (!initialized) {
    throw new Error('Coign not initialized. Call init() first.');
  }
  if (warmUpPromise) return warmUpPromise;

  warmUpPromise = (async () => {
    const cfg = getConfig();
    ensureOutline();
    const useQuick = cfg.quick !== false;

    if (useQuick) {
      if (getTier() === 'none') {
        await loadModel('quick', cfg);
      }
      // Defer full download so it does not contend with first ask
      if (getTier() === 'quick') {
        scheduleIdleUpgrade();
      }
    } else {
      if (getTier() !== 'full') {
        await loadModel('full', cfg);
      }
      setUpgradeStatus('full');
    }
  })();

  return warmUpPromise;
}

function clearIdleUpgrade(): void {
  if (idleUpgradeHandle != null) {
    clearTimeout(idleUpgradeHandle);
    idleUpgradeHandle = null;
  }
}

function scheduleIdleUpgrade(): void {
  if (upgradePromise || getTier() === 'full' || getConfig().quick === false) return;
  clearIdleUpgrade();

  const kick = () => {
    idleUpgradeHandle = null;
    if (asksInFlight > 0) {
      idleUpgradeHandle = setTimeout(kick, 400);
      return;
    }
    void startFullUpgrade();
  };

  // Defer so first ask after warmUp is not contended with full download.
  idleUpgradeHandle = setTimeout(kick, 1500);
}

function startFullUpgrade(): Promise<void> {
  const cfg = getConfig();
  if (cfg.quick === false) return Promise.resolve();
  if (getTier() === 'full') return Promise.resolve();
  if (upgradePromise) return upgradePromise;
  if (getTier() !== 'quick') return Promise.resolve();

  clearIdleUpgrade();

  upgradePromise = (async () => {
    try {
      setUpgradeStatus('upgrading');
      await loadModel('full', cfg);
      emit('upgraded', { tier: 'full' });
      if (cfg.onUpgraded) cfg.onUpgraded({ tier: 'full' });
      setUpgradeStatus('full');
      setStatus('ready');
    } catch (e) {
      console.error('[Coign] Full model upgrade failed:', e);
      setUpgradeStatus('quick');
      // Keep quick tier usable
    } finally {
      upgradePromise = null;
    }
  })();

  return upgradePromise;
}

async function waitForUsableTier(): Promise<ModelTier> {
  // If currently swapping (tier none but loading), wait
  while (getTier() === 'none' && getIsLoading()) {
    await new Promise((r) => setTimeout(r, 50));
  }
  if (getTier() === 'none') {
    await warmUp();
  }
  // Wait through swap gap during upgrade (main-thread path)
  while (getTier() === 'none') {
    await new Promise((r) => setTimeout(r, 50));
    if (!getIsLoading() && getTier() === 'none') {
      await warmUp();
    }
  }
  return getTier();
}

async function runAsk(question: string, opts?: AskOptions): Promise<string> {
  setThinking(true);
  const useStream = opts?.stream ?? getConfig().stream !== false;
  let streaming = false;
  asksInFlight++;

  try {
    emit('ask', { question });
    const start = performance.now();
    const currentConfig = getConfig();
    ensureOutline();
    const outline = outlineStore.get('current')!;
    const promptBase = currentConfig.prompt ?? DEFAULT_PROMPT;
    const { quickSystem, fullSystem } = getCachedPrompts(outline, promptBase);

    await waitForUsableTier();
    const tier = getTier();

    const history = loadHistory();
    const onToken = (token: string) => {
      if (useStream) {
        if (!streaming) {
          startAssistantStream();
          streaming = true;
          setThinking(false);
        }
        appendStreamToken(token);
      }
      opts?.onToken?.(token);
    };

    let answer: string;
    let toolCalls: import('./types.js').ToolCall[] = [];

    if (tier === 'quick') {
      const messages: import('@mlc-ai/web-llm').ChatCompletionMessageParam[] = [
        { role: 'system', content: quickSystem },
        ...history.map((h) => ({ role: h.role as 'user' | 'assistant', content: h.content })),
        { role: 'user', content: question },
      ];
      answer = await chatCompletion(messages, {
        stream: useStream,
        onToken,
        phase: 'quick',
      });
    } else {
      const messages: import('@mlc-ai/web-llm').ChatCompletionMessageParam[] = [
        { role: 'system', content: fullSystem },
        ...history.map((h) => ({ role: h.role as 'user' | 'assistant', content: h.content })),
        { role: 'user', content: question },
      ];
      const result = await runManualToolLoop(messages, {
        stream: useStream,
        onToken,
      });
      answer = result.answer;
      toolCalls = result.toolCalls;
    }

    if (streaming) {
      finishAssistantStream();
      streamedAnswerPending = true;
    }

    const durationMs = Math.round(performance.now() - start);
    history.push({ role: 'user', content: question, timestamp: Date.now() });
    history.push({ role: 'assistant', content: answer, toolCalls, timestamp: Date.now() });
    if (currentConfig.persistHistory !== false) {
      saveHistory(history);
    }

    emit('answer', { answer, toolCalls, durationMs, tier });

    // After first quick answer, start full upgrade (also clears idle wait)
    if (tier === 'quick') {
      void startFullUpgrade();
    }

    return answer;
  } finally {
    asksInFlight--;
    if (streaming) {
      // already finished
    } else {
      setThinking(false);
    }
  }
}

function createSDK(_config: CoignConfig): CoignSDK {
  return {
    async ask(question: string, opts?: AskOptions): Promise<string> {
      // Serialize asks so upgrade gaps don't interleave badly
      const result = askQueue.then(() => runAsk(question, opts));
      askQueue = result.catch(() => {});
      return result;
    },

    warmUp,

    async hasModelCached(tier: 'quick' | 'full' = 'quick'): Promise<boolean> {
      return engineHasModelCached(tier, getConfig());
    },

    getTier,

    update(patch) {
      patchConfig(patch);
    },

    destroy() {
      eventUnsubs.forEach((unsub) => unsub());
      eventUnsubs = [];
      clearAllListeners();
      clearIdleUpgrade();
      void destroyEngine();
      destroyWidget();
      resetConfig();
      invalidatePromptCache();
      invalidateSearchIndex();
      sdkInstance = null;
      initialized = false;
      warmUpPromise = null;
      upgradePromise = null;
      askQueue = Promise.resolve();
      asksInFlight = 0;
    },

    show() {
      showWidget();
    },
    hide() {
      hideWidget();
    },
    open() {
      openPanel();
    },
    close() {
      closePanel();
    },

    mount(selector) {
      mountInline(selector);
    },
    unmount() {
      unmountInline();
    },

    tool(def) {
      const unsub = registerHost(def);
      invalidatePromptCache();
      return () => {
        unsub();
        invalidatePromptCache();
      };
    },

    on(event, cb) {
      return on(event as CoignEvent, cb);
    },

    config(patch) {
      patchConfig(patch);
    },

    clearHistory() {
      clearHistory();
    },

    exportHistory() {
      return exportHistory();
    },

    isInitialized() {
      return initialized;
    },
    isReady() {
      return initialized && getTier() !== 'none' && !getIsLoading();
    },

    async retryInit(configOverride?: Partial<CoignConfig>): Promise<CoignSDK> {
      if (initialized) return sdkInstance!;
      return init(configOverride);
    },

    async swapModel(model: string): Promise<void> {
      if (!initialized || !sdkInstance) {
        throw new Error('Coign not initialized. Call init() first.');
      }
      const resolvedName = resolvePresetName(model);
      const resolvedModel = resolvePreset(resolvedName);
      patchConfig({ model: resolvedModel, preset: resolvedName });
      warmUpPromise = null;
      upgradePromise = null;
      await loadModel('full', getConfig());
      emit('upgraded', { tier: 'full' });
      setUpgradeStatus('full');
    },

    version: VERSION,
  };
}

/**
 * Initialize Coign shell. Never awaits model download.
 * Returns when widget is mounted and `shellReady` has fired.
 */
export async function init(config: CoignConfig | Partial<CoignConfig> = {}): Promise<CoignSDK> {
  if (initialized && sdkInstance) {
    return sdkInstance;
  }

  // Merge + patch so ask() sees prompt and all options
  const merged: Partial<CoignConfig> = {
    ...config,
    preset: config.preset || config.model || 'coign-lite',
    model: config.model || config.preset || 'coign-lite',
  };
  patchConfig(merged);

  setHostNamespace(typeof location !== 'undefined' ? location.origin : 'unknown');

  registerBuiltin('searchPage', searchPageTool);
  registerBuiltin('getPageOutline', getPageOutlineTool);
  registerBuiltin('getElement', getElementTool);

  maybeOutlineAtInit();

  const cfg = getConfig() as CoignConfig;
  sdkInstance = createSDK(cfg);
  initialized = true;

  wireConfigCallbacks(merged);

  // Widget mounts immediately — shell ready before GPU probe / model work
  if (cfg.widget !== false) {
    await loadWidgetUi();
    createWidgetUI(cfg, (question) => {
      void sdkInstance!.ask(question);
    });

    eventUnsubs.push(
      on('answer', ({ answer }) => {
        if (streamedAnswerPending) {
          streamedAnswerPending = false;
          return;
        }
        addAssistantMessage(answer);
      }),
      on('toolCall', ({ name, args }) => addToolCallMessage(name, JSON.stringify(args))),
      on('toolResult', ({ name, result }) => addToolResultMessage(name, JSON.stringify(result))),
      on('modelReady', ({ tier }) => {
        setStatus('ready');
        setUpgradeStatus(tier === 'full' ? 'full' : 'quick');
      }),
      on('upgraded', () => {
        setUpgradeStatus('full');
      })
    );

    setOnOpenCallback(() => {
      if (getConfig().engage === 'open') {
        void warmUp();
      }
      // Gesture: preload worker JS parse early
      if ((getConfig().engineMode ?? 'worker') === 'worker') {
        void preloadWorkerEngine();
      }
    });
  }

  emit('shellReady', {});
  if (merged.onShellReady) merged.onShellReady();
  markApiReady();

  // Non-blocking GPU / browser support check (does not delay shellReady)
  void checkSupport().then((support) => {
    if (!support.supported) {
      const err = Object.assign(new Error(support.reason ?? 'WebGPU unsupported'), {
        kind: 'browser_unsupported' as const,
        cause: new Error(support.reason ?? 'WebGPU unsupported'),
      }) as CoignError;
      if (merged.onError) merged.onError(err);
      emit('error', err);
      console.warn(
        '[Coign] WebGPU unavailable — chat shell mounted, but model load will fail:',
        support.reason
      );
    }
  });

  // Preload worker bootstrap on init (parse ~2MB later on engage still deferred until warmUp)
  if ((cfg.engineMode ?? 'worker') === 'worker') {
    void preloadWorkerEngine();
  }

  // Eager warm when engage === 'init'
  if (cfg.engage === 'init') {
    void warmUp();
  }

  return sdkInstance;
}

export function tool(def: ToolDefinition): () => void {
  const unsub = registerHost(def);
  invalidatePromptCache();
  return () => {
    unsub();
    invalidatePromptCache();
  };
}

export async function ask(question: string, opts?: AskOptions): Promise<string> {
  if (!sdkInstance) {
    throw new Error('Coign not initialized. Call init() first.');
  }
  return sdkInstance.ask(question, opts);
}

export async function hasModelCached(tier: 'quick' | 'full' = 'quick'): Promise<boolean> {
  return engineHasModelCached(tier, getConfig());
}

export { getTier, cancelEngineInit };

export function isInitialized(): boolean {
  return initialized;
}

export function isReady(): boolean {
  return initialized && getTier() !== 'none' && !getIsLoading();
}

export async function retryInit(configOverride?: Partial<CoignConfig>): Promise<CoignSDK> {
  if (initialized && sdkInstance) return sdkInstance;
  return init(configOverride);
}

export async function swapModel(model: string): Promise<void> {
  if (!initialized || !sdkInstance) {
    throw new Error('Coign not initialized. Call init() first.');
  }
  return sdkInstance.swapModel(model);
}

export function destroy(): void {
  sdkInstance?.destroy();
  if (api) {
    resetQueueState(api);
  }
}

export function mount(selector: string): void {
  sdkInstance?.mount(selector);
}

export function unmount(): void {
  sdkInstance?.unmount();
}

export function show(): void {
  sdkInstance?.show();
}

export function hide(): void {
  sdkInstance?.hide();
}

export function open(): void {
  sdkInstance?.open();
}

export function close(): void {
  sdkInstance?.close();
}

export function config(patch: Partial<CoignConfig>): void {
  patchConfig(patch);
  sdkInstance?.update(patch);
  if (patch.theme) {
    applyTheme(patch.theme);
  }
}

export async function searchPage(query: string): Promise<any> {
  return searchPageTool.execute({ query });
}

export async function getPageOutline(): Promise<string> {
  return getPageOutlineTool.execute({});
}

export async function getElement(selector: string): Promise<any> {
  return getElementTool.execute({ selector });
}

export async function createWidget(): Promise<void> {
  await loadWidgetUi();
  createWidgetUI(getConfig() as CoignConfig, (question) => {
    void sdkInstance?.ask(question);
  });
}

export function showWidget(): void {
  showWidgetUI();
}

export function hideWidget(): void {
  hideWidgetUI();
}

export function destroyWidget(): void {
  destroyWidgetUI();
}

export { showConfirmDialog, on };

export { clearHistory, exportHistory };

export { VERSION as version };

export { resolveFullModelId };

// --- Global API object for IIFE / queue-replay ---

api = {
  _ready: false,
  _callbacks: [],
  init,
  ask,
  warmUp,
  hasModelCached,
  getTier,
  tool,
  destroy,
  mount,
  unmount,
  show,
  hide,
  open,
  close,
  config,
  on,
  clearHistory,
  exportHistory,
  isInitialized,
  isReady,
  retryInit,
  swapModel,
  cancelEngineInit,
  searchPage,
  getPageOutline,
  getElement,
  showConfirmDialog,
  createWidget,
  showWidget,
  hideWidget,
  destroyWidget,
  version: VERSION,
};

const realAPI = createRealAPI(api);
Object.assign(realAPI, api);

(realAPI as any).checkSupport = checkSupport;

if (typeof window !== 'undefined') {
  const w = window as any;
  if (!w.Coign || !w.Coign.version) {
    const existingQueue = w.Coign && w.Coign.q ? w.Coign.q.slice() : [];
    w.Coign = realAPI;
    if (existingQueue.length > 0) {
      w.Coign.q = existingQueue;
      replayQueue(api);
    }
  }
}

export default realAPI;
